import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Music, PawPrint, Sprout, TreePine, Mountain, Dumbbell, Heart, Sparkles } from "lucide-react";
import DecorativeDivider from "@/components/DecorativeDivider";
import PhotoCarousel from "@/components/PhotoCarousel";

const musicPhotos = [
  {
    src: "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/jbx6qr33_Music%201.JPG",
    alt: "Playing guitar at a celebration",
    caption: "Performing at a family celebration",
    aspect: "4/5",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/fh20dkxa_Music%202.JPG",
    alt: "Playing flute",
    caption: "Playing the flute",
    aspect: "4/5",
  },
];

const animalPhotos = [
  {
    src: "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/yvgqg3qn_Animal%20lover%202.jpg",
    alt: "With golden retriever",
    caption: "With a furry friend",
    aspect: "4/5",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/fz87c4bu_Animal%20Lover%201.jpg",
    alt: "Holding a baby goat",
    caption: "At an animal farm",
    aspect: "4/5",
  },
];

const outdoorPhotos = [
  {
    src: "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/2zgjw5tn_Trekking.jpg",
    alt: "Trekking in the hills",
    caption: "Trekking and exploring the outdoors",
    aspect: "4/5",
  },
];

const footballPhotos = [
  {
    src: "https://customer-assets.emergentagent.com/job_vani-matrimony/artifacts/h5xq6uxa_Football.JPG",
    alt: "At the football ground",
    caption: "On the football pitch",
    aspect: "4/5",
  },
];

const interests = [
  { icon: Music, title: "Music", desc: "Plays Guitar, Cajon & Tabla" },
  { icon: PawPrint, title: "Animal Lover", desc: "Deeply fond of animals" },
  { icon: Sprout, title: "Gardening", desc: "Enjoys nurturing plants and green spaces" },
  { icon: TreePine, title: "Farming & Nature", desc: "Connected to agricultural roots" },
  { icon: Mountain, title: "Trekking & Outdoors", desc: "Loves trekking and outdoor activities" },
  { icon: Dumbbell, title: "Football", desc: "Passionate Arsenal fan" },
  { icon: Sparkles, title: "Spirituality", desc: "Values spiritual growth and inner peace" },
  { icon: Heart, title: "Personal Growth", desc: "Committed to self-improvement" },
];

export default function InterestsValues() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="py-12 md:py-16 px-4 sm:px-6 md:px-12"
      style={{ background: "#F7F4EF" }}
      data-testid="interests-section"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={visible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p
            className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-2 font-semibold"
            style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
          >
            Passions
          </p>
          <h2
            className="text-3xl md:text-4xl font-normal tracking-tight mb-6 md:mb-8"
            style={{ fontFamily: "'Playfair Display', serif", color: "#2D2D2A" }}
          >
            Interests & Values
          </h2>
        </motion.div>

        {/* Photo Carousels Row - Music & Animal Lover */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 mb-6 sm:mb-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <p
              className="text-xs tracking-[0.15em] uppercase font-semibold mb-2"
              style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
            >
              Music
            </p>
            <PhotoCarousel photos={musicPhotos} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            <p
              className="text-xs tracking-[0.15em] uppercase font-semibold mb-2"
              style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
            >
              Animal Lover
            </p>
            <PhotoCarousel photos={animalPhotos} />
          </motion.div>
        </div>

        {/* Photo Row - Outdoors & Football */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 mb-6 sm:mb-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            <p
              className="text-xs tracking-[0.15em] uppercase font-semibold mb-2"
              style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
            >
              Nature & Outdoors
            </p>
            <PhotoCarousel photos={outdoorPhotos} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            <p
              className="text-xs tracking-[0.15em] uppercase font-semibold mb-2"
              style={{ color: "#8A9A5B", fontFamily: "'Manrope', sans-serif" }}
            >
              Football
            </p>
            <PhotoCarousel photos={footballPhotos} />
          </motion.div>
        </div>

        {/* Interests Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3.5 mt-6 sm:mt-8">
          {interests.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 12 }}
                animate={visible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.05 }}
                className="p-3.5 sm:p-4 rounded-xl text-center transition-all duration-300 hover:shadow-xs"
                style={{ background: "#FDFBF7", border: "1px solid #E6E2DD" }}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-2" style={{ color: "#8A9A5B" }} strokeWidth={1.5} />
                <p className="text-xs sm:text-sm font-medium mb-0.5" style={{ color: "#2D2D2A", fontFamily: "'Manrope', sans-serif" }}>
                  {item.title}
                </p>
                <p className="text-[11px] sm:text-xs font-light leading-snug" style={{ color: "#999999", fontFamily: "'Manrope', sans-serif" }}>
                  {item.desc}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={visible ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="mt-6 sm:mt-8 text-center"
        >
          <p
            className="text-sm sm:text-base md:text-lg leading-relaxed font-light italic max-w-xl mx-auto"
            style={{ color: "#2D2D2A", fontFamily: "'Playfair Display', serif", opacity: 0.8 }}
          >
            "Seeking a partner who values mutual respect, emotional maturity, and shared growth."
          </p>
        </motion.div>

        <div className="mt-8 sm:mt-10">
          <DecorativeDivider />
        </div>
      </div>
    </section>
  );
}
